import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { objectToQueryString } from 'src/utils/uri';
import {
  APP_COUNTS_KEY,
  RESULTS_TAB_DATA_COUNTS_KEY,
  WDAC_APP_QUERY_KEY,
  WDAC_PACKAGED_APP_QUERY_KEY,
  WDAC_TABS_DATA_COUNTS_KEY,
} from 'src/constants/query';
import { MANAGED_BASE_POLICIES_QUERY_KEY } from 'src/hooks/environmentWdacPolicy';
import { APPLICATION_TRUST_STATUS } from 'src/utils/enums/applicationTrustStatus';

export const WDAC_UNMATCHED_FILES_QUERY_KEY = 'wdacUnmatchedFiles';
export const WDAC_UNMATCHED_FILES_SUMMARY_QUERY_KEY = 'wdacUnmatchedFilesSummary';
export const WDAC_UNMATCHED_FILE_SUMMARY_QUERY_KEY = 'wdacUnmatchedFileSummary';
export const WDAC_UNMATCHED_FILE_SIGNERS_QUERY_KEY = 'wdacUnmatchedFileSigners';
export const WDAC_UNMATCHED_FILE_MATCHED_APPS_QUERY_KEY = 'wdacUnmatchedFileMatchedApps';
export const WDAC_UNMATCHED_FILE_EVENTS_QUERY_KEY = 'wdacUnmatchedFileEvents';
export const WDAC_UNMATCHED_CODE_SIGNERS_QUERY_KEY = 'wdacUnmatchedCodeSigners';
export const WDAC_UNMATCHED_CODE_SIGNER_FILES_QUERY_KEY = 'wdacUnmatchedCodeSignerFiles';
export const WDAC_UNMATCHED_MATCHED_APPS_QUERY_KEY = 'wdacUnmatchedMatchedApps';
export const WDAC_UNMATCHED_MATCHED_APP_FILES_QUERY_KEY = 'wdacUnmatchedMatchedAppFiles';
export const WDAC_UNMATCHED_CODE_SIGNER_GROUP_FILE_COUNT_QUERY_KEY =
  'wdacUnmatchedCodeSignerGroupFileCount';
export const WDAC_UNMATCHED_MATCHED_APP_GROUP_FILE_COUNT_QUERY_KEY =
  'wdacUnmatchedMatchedAppGroupFileCount';
export const WDAC_RESULTS_QUERY_KEY = 'results';
export const WDAC_PACKAGED_APPS_SUMMARY_QUERY_KEY = 'wdacPackagedAppsSummary';
const WDAC_PACKAGED_APP_SIGNATURE_KINDS_QUERY_KEY = 'wdacPackagedAppSignatureKinds';
const UNMATCHED_FILES_POLICY_QUERY_KEY = 'unmatchedFilesPolicy';
const PACKAGED_APPS_POLICY_QUERY_KEY = 'packagedAppsPolicy';
const WIN32_APP_POLICY_QUERY_KEY = 'win32AppPolicy';
const WIN32_APP_FILE_SETS_QUERY_KEY = 'win32AppFileSets';
const WIN32_APP_FILE_SET_LIST_QUERY_KEY = 'win32AppFileSetList';
const WIN32_APP_FILES_QUERY_KEY = 'win32AppFiles';
const WIN32_FILE_SET_DETAIL_QUERY_KEY = 'win32FileSetDetail';
const WIN32_FILE_SET_SECTION_QUERY_KEY = 'win32FileSetSection';
const WIN32_APP_FILE_DETAIL_QUERY_KEY = 'win32AppFileDetail';
const WIN32_APP_FILE_SECTION_QUERY_KEY = 'win32AppFileSection';
const WIN32_APP_WDAC_FILE_COUNTS_QUERY_KEY = 'win32AppWdacFileCounts';
const WIN32_APP_WDAC_FILES_QUERY_KEY = 'win32AppWdacFiles';
const WIN32_APP_WDAC_IMPACTED_DEVICES_QUERY_KEY = 'win32AppWdacImpactedDevices';
const APP_PORTFOLIO_QUERY_KEY = 'appPortfolio';
const APP_PORTFOLIO_VERSIONS_QUERY_KEY = 'appPortfolioVersions';
const APP_PORTFOLIO_VERSION_LIST_QUERY_KEY = 'appPortfolioVersionList';

const getWdacResults = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/wdacResults?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFiles = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFilesSummary = async ({ customerEnvId, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/summary?${objectToQueryString(query)}`, {
    ...(signal ? { signal } : {}),
  });

const postUnmatchedFileStatuses = ({
  customerEnvId,
  customerEnvironmentUnmatchedFileStatusIds,
  status,
  notes,
}) =>
  api.post(`/wdac/${customerEnvId}/unmatchedFiles/updateStatus`, {
    customerEnvironmentUnmatchedFileStatusIds,
    status,
    notes,
  });

const postUnmatchedFileStatusesForFilter = ({ customerEnvId, query, payload: { status, notes } }) =>
  api.post(
    `/wdac/${customerEnvId}/unmatchedFiles/updateStatusForFilter?${objectToQueryString(query)}`,
    { status, notes },
  );

const postUnmatchedCodeSignerGroupStatuses = ({
  customerEnvId,
  query,
  payload: { status, notes },
}) =>
  api.post(
    `/wdac/${customerEnvId}/unmatchedCodeSigners/updateStatus?${objectToQueryString(query)}`,
    { status, notes },
  );

const postUnmatchedMatchedAppGroupStatuses = ({
  customerEnvId,
  query,
  payload: { status, notes },
}) =>
  api.post(
    `/wdac/${customerEnvId}/unmatchedMatchedApps/updateStatus?${objectToQueryString(query)}`,
    { status, notes },
  );

const getUnmatchedCodeSignerGroupFileCount = ({ customerEnvId, payload }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedCodeSigners/fileCount?${objectToQueryString(payload)}`);

const getUnmatchedMatchedAppGroupFileCount = ({ customerEnvId, payload }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedMatchedApps/fileCount?${objectToQueryString(payload)}`);

const getUnmatchedFileSummary = async ({ customerEnvId, statusId, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/${statusId}/summary`, {
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFileSigners = async ({ customerEnvId, fileId, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/${fileId}/signers`, {
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFileMatchedApps = async ({ customerEnvId, fileId, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/${fileId}/matchedApps`, {
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFileEvents = async ({ customerEnvId, fileId, page, limit, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/${fileId}/events`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedCodeSigners = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedCodeSigners?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedCodeSignerFiles = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedCodeSigners/files?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedMatchedApps = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedMatchedApps?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedMatchedAppFiles = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedMatchedApps/files?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getUnmatchedFilesPolicy = ({ customerEnvId, signal }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/policy`, {
    ...(signal ? { signal } : {}),
  });

const postGenerateUnmatchedFilesCatalog = ({ customerEnvId, basePolicyIds }) =>
  api.post(`/wdac/${customerEnvId}/unmatchedFiles/policy/generate`, {
    basePolicyIds,
  });

const getUnmatchedFilesCatalogZip = ({ customerEnvId }) =>
  api.get(`/wdac/${customerEnvId}/unmatchedFiles/policy/download`, { responseType: 'blob' });

const getPackagedApps = async ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/packagedApp/${customerEnvId}/all?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getPackagedAppsCsv = ({ customerEnvId, query }) =>
  api.get(`/packagedApp/${customerEnvId}/export?${objectToQueryString(query)}`, {
    responseType: 'blob',
  });

const getPackagedAppsSummary = async ({ customerEnvId, query, signal }) =>
  api.get(`/packagedApp/${customerEnvId}/summary?${objectToQueryString(query)}`, {
    ...(signal ? { signal } : {}),
  });

const getPackagedAppSignatureKinds = async ({ customerEnvId, signal }) =>
  api.get(`/packagedApp/${customerEnvId}/signatureKinds`, {
    ...(signal ? { signal } : {}),
  });

const postPackagedAppStatuses = ({ customerEnvId, globalPackagedAppIds, status, notes }) =>
  api.post(`/packagedApp/${customerEnvId}/bulkUpdateGlobalPackagedAppStatus`, {
    globalPackagedAppIds,
    status,
    notes,
  });

const getPackagedAppsPolicy = ({ customerEnvId, signal }) =>
  api.get(`/wdac/${customerEnvId}/packagedApps/policy`, {
    ...(signal ? { signal } : {}),
  });

const postGeneratePackagedAppsPolicy = ({ customerEnvId, basePolicyIds }) =>
  api.post(`/wdac/${customerEnvId}/packagedApps/policy/generate`, {
    basePolicyIds,
  });

const getPackagedAppsPolicyZip = ({ customerEnvId }) =>
  api.get(`/wdac/${customerEnvId}/packagedApps/policy/download`, { responseType: 'blob' });

const getWin32AppPolicy = ({ customerEnvId, globalApplicationVersionId, signal }) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/policy`, {
    ...(signal ? { signal } : {}),
  });

const getWin32AppCatalogZip = ({ customerEnvId, globalApplicationVersionId }) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/policy/download`, {
    responseType: 'blob',
  });

const getWin32FileSetDetail = ({ customerEnvId, globalApplicationVersionId, fileSetId, signal }) =>
  api.get(`/wdac/${customerEnvId}/${globalApplicationVersionId}/fileSets/${fileSetId}`, {
    ...(signal ? { signal } : {}),
  });

const getWin32FileSetSection = ({
  customerEnvId,
  globalApplicationVersionId,
  fileSetId,
  section,
  page,
  limit,
  signal,
}) =>
  api.get(`/wdac/${customerEnvId}/${globalApplicationVersionId}/fileSets/${fileSetId}/${section}`, {
    params: { Page: page, Limit: limit },
    ...(signal ? { signal } : {}),
  });

const getWin32AppFileDetail = ({ customerEnvId, globalApplicationVersionId, fileId, signal }) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/files/${fileId}`, {
    ...(signal ? { signal } : {}),
  });

const getWin32AppFileSigners = ({ customerEnvId, globalApplicationVersionId, fileId, signal }) =>
  api.get(
    `/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/files/${fileId}/signers`,
    {
      ...(signal ? { signal } : {}),
    },
  );

// section is paths or devices.
const getWin32AppFileSection = ({
  customerEnvId,
  globalApplicationVersionId,
  fileId,
  section,
  page,
  limit,
  signal,
}) =>
  api.get(
    `/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/files/${fileId}/${section}`,
    {
      params: { page, limit },
      ...(signal ? { signal } : {}),
    },
  );

const getWin32AppFileSets = ({ customerEnvId, globalApplicationVersionId, signal }) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/fileSets`, {
    ...(signal ? { signal } : {}),
  });

const getWin32AppFiles = ({
  customerEnvId,
  globalApplicationVersionId,
  sources,
  isSigned,
  search,
  sortColumn,
  sortDesc,
  page,
  limit,
  signal,
}) =>
  api.get(
    `/wdac/${customerEnvId}/${globalApplicationVersionId}/files?${objectToQueryString({
      sources,
    })}`,
    {
      params: {
        page,
        limit,
        ...(isSigned != null ? { isSigned } : {}),
        ...(search ? { search } : {}),
        ...(sortColumn ? { sortColumn, sortDesc } : {}),
      },
      ...(signal ? { signal } : {}),
    },
  );

// Statuses go through the query string by hand: axios would send them as statuses[]=, which the
// controller does not bind.
const getWin32AppFileSetList = ({
  customerEnvId,
  globalApplicationVersionId,
  statuses,
  search,
  page,
  limit,
  signal,
}) =>
  api.get(
    `/wdac/${customerEnvId}/${globalApplicationVersionId}/fileSets?${objectToQueryString({
      statuses,
    })}`,
    {
      params: { page, limit, ...(search ? { search } : {}) },
      ...(signal ? { signal } : {}),
    },
  );

const getWin32AppWdacFileCounts = ({
  customerEnvId,
  globalApplicationVersionId,
  reportedWindow,
  isSigned,
  signal,
}) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/wdacFiles/counts`, {
    params: {
      ...(reportedWindow ? { reportedWindow } : {}),
      ...(isSigned != null ? { isSigned } : {}),
    },
    ...(signal ? { signal } : {}),
  });

const getWin32AppWdacFiles = ({
  customerEnvId,
  globalApplicationVersionId,
  isCovered,
  reportedWindow,
  isSigned,
  search,
  sortColumn,
  sortDesc,
  page,
  limit,
  signal,
}) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/wdacFiles`, {
    params: {
      page,
      limit,
      ...(reportedWindow ? { reportedWindow } : {}),
      ...(isSigned != null ? { isSigned } : {}),
      ...(isCovered != null ? { isCovered } : {}),
      ...(search ? { search } : {}),
      ...(sortColumn ? { sortColumn, sortDesc } : {}),
    },
    ...(signal ? { signal } : {}),
  });

const getWin32AppWdacImpactedDevices = ({
  customerEnvId,
  globalApplicationVersionId,
  page,
  limit,
  signal,
}) =>
  api.get(`/wdac/${customerEnvId}/win32Apps/${globalApplicationVersionId}/wdacImpactedDevices`, {
    params: { page, limit },
    ...(signal ? { signal } : {}),
  });

const getAppPortfolio = ({
  customerEnvId,
  page,
  limit,
  search,
  statuses,
  sources,
  sortColumn,
  sortDesc,
  signal,
}) =>
  api.get(`/wdac/${customerEnvId}/appPortfolio?${objectToQueryString({ statuses, sources })}`, {
    params: {
      page,
      limit,
      ...(search ? { search } : {}),
      ...(sortColumn ? { sortColumn } : {}),
      sortDesc,
    },
    ...(signal ? { signal } : {}),
  });

const getAppPortfolioVersions = ({
  customerEnvId,
  globalApplicationVersionId,
  statuses,
  sources,
  signal,
}) =>
  api.get(
    `/wdac/${customerEnvId}/appPortfolio/${globalApplicationVersionId}/versions?${objectToQueryString(
      { statuses, sources },
    )}`,
    {
      ...(signal ? { signal } : {}),
    },
  );

const getAppPortfolioVersionList = ({
  customerEnvId,
  page,
  limit,
  search,
  statuses,
  sources,
  sortColumn,
  sortDesc,
  signal,
}) =>
  api.get(
    `/wdac/${customerEnvId}/appPortfolio/versions?${objectToQueryString({ statuses, sources })}`,
    {
      params: {
        page,
        limit,
        ...(search ? { search } : {}),
        ...(sortColumn ? { sortColumn } : {}),
        sortDesc,
      },
      ...(signal ? { signal } : {}),
    },
  );

// Trusting sends the rest: which imports are enabled, an import to create from telemetry, and the
// base policies the catalog is versioned for. A null leaves that part as it is.
export const postGlobalAppStatus = ({
  customerEnvId,
  globalApplicationVersionId,
  status,
  notes,
  enabledCustomerEnvironmentApplicationIds,
  import: importScope,
  basePolicyIds,
}) =>
  api.post(`/wdac/${customerEnvId}/${globalApplicationVersionId}/updateGlobalAppStatus`, {
    status,
    notes,
    enabledCustomerEnvironmentApplicationIds: enabledCustomerEnvironmentApplicationIds ?? null,
    import: importScope ?? null,
    basePolicyIds: basePolicyIds ?? null,
  });

// Only the total matters, so this asks for a single row rather than a page of them.
export const useWdacResultsCount = ({ apiQuery, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_RESULTS_QUERY_KEY, 'count', apiQuery, customerEnvId],
    ({ signal }) => getWdacResults({ customerEnvId, page: 1, limit: 1, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      select: data => data?.totalCount || 0,
    },
  );
};

export const useInfiniteWdacResults = ({ apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_RESULTS_QUERY_KEY, apiQuery, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getWdacResults({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useInfiniteUnmatchedFiles = ({ apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_UNMATCHED_FILES_QUERY_KEY, apiQuery, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getUnmatchedFiles({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useUnmatchedFilesSummary = ({ apiQuery, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_FILES_SUMMARY_QUERY_KEY, apiQuery, customerEnvId],
    ({ signal }) =>
      getUnmatchedFilesSummary({
        customerEnvId,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );
};

export const useUnmatchedFilesPolicy = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [UNMATCHED_FILES_POLICY_QUERY_KEY, customerEnvId],
    ({ signal }) => getUnmatchedFilesPolicy({ customerEnvId, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

const invalidateUnmatchedQueries = queryClient => {
  [
    WDAC_UNMATCHED_FILES_QUERY_KEY,
    WDAC_UNMATCHED_FILES_SUMMARY_QUERY_KEY,
    WDAC_UNMATCHED_CODE_SIGNERS_QUERY_KEY,
    WDAC_UNMATCHED_CODE_SIGNER_FILES_QUERY_KEY,
    WDAC_UNMATCHED_MATCHED_APPS_QUERY_KEY,
    WDAC_UNMATCHED_MATCHED_APP_FILES_QUERY_KEY,
    WDAC_UNMATCHED_FILE_SUMMARY_QUERY_KEY,
    UNMATCHED_FILES_POLICY_QUERY_KEY,
  ].forEach(queryKey => queryClient.invalidateQueries({ queryKey: [queryKey], exact: false }));
};

export const useGenerateUnmatchedFilesCatalog = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ basePolicyIds }) =>
      postGenerateUnmatchedFilesCatalog({
        customerEnvId,
        basePolicyIds,
      }),
    // The request only writes the new policy versions; the generation job builds the catalog and
    // stamps TrustedAsOfVersion afterwards, so the catalog-drift warning clears on a later refetch.
    onSuccess: () => invalidateUnmatchedQueries(queryClient),
  });
};

export const useDownloadUnmatchedFilesCatalog = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation({
    mutationFn: () => getUnmatchedFilesCatalogZip({ customerEnvId }),
  });
};

export const usePackagedAppsPolicy = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [PACKAGED_APPS_POLICY_QUERY_KEY, customerEnvId],
    ({ signal }) => getPackagedAppsPolicy({ customerEnvId, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

const invalidatePackagedAppQueries = queryClient => {
  [
    WDAC_PACKAGED_APP_QUERY_KEY,
    WDAC_PACKAGED_APPS_SUMMARY_QUERY_KEY,
    PACKAGED_APPS_POLICY_QUERY_KEY,
    WDAC_TABS_DATA_COUNTS_KEY,
  ].forEach(queryKey => queryClient.invalidateQueries({ queryKey: [queryKey], exact: false }));
};

export const useInfinitePackagedApps = ({ apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_PACKAGED_APP_QUERY_KEY, apiQuery, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getPackagedApps({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useExportPackagedApps = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation({
    mutationFn: ({ apiQuery }) => getPackagedAppsCsv({ customerEnvId, query: apiQuery }),
  });
};

export const usePackagedAppsSummary = ({ apiQuery, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_PACKAGED_APPS_SUMMARY_QUERY_KEY, apiQuery, customerEnvId],
    ({ signal }) => getPackagedAppsSummary({ customerEnvId, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );
};

export const usePackagedAppSignatureKinds = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_PACKAGED_APP_SIGNATURE_KINDS_QUERY_KEY, customerEnvId],
    ({ signal }) => getPackagedAppSignatureKinds({ customerEnvId, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUpdatePackagedAppStatuses = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ globalPackagedAppIds, status, notes }) =>
      postPackagedAppStatuses({ customerEnvId, globalPackagedAppIds, status, notes }),
    onSuccess: () => invalidatePackagedAppQueries(queryClient),
  });
};

export const useGeneratePackagedAppsPolicy = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ basePolicyIds }) =>
      postGeneratePackagedAppsPolicy({
        customerEnvId,
        basePolicyIds,
      }),
    // The request only writes the new policy versions; the generation job builds the policy and
    // stamps TrustedAsOfVersion afterwards, so the drift warning clears on a later refetch.
    onSuccess: () => invalidatePackagedAppQueries(queryClient),
  });
};

export const useDownloadPackagedAppsPolicy = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation({
    mutationFn: () => getPackagedAppsPolicyZip({ customerEnvId }),
  });
};

export const useUpdateUnmatchedFileStatuses = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerEnvironmentUnmatchedFileStatusIds, status, notes }) =>
      postUnmatchedFileStatuses({
        customerEnvId,
        customerEnvironmentUnmatchedFileStatusIds,
        status,
        notes,
      }),
    onSuccess: () => invalidateUnmatchedQueries(queryClient),
  });
};

export const useUpdateUnmatchedFileStatusesForFilter = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ query, payload }) =>
      postUnmatchedFileStatusesForFilter({ customerEnvId, query, payload }),
    onSuccess: () => invalidateUnmatchedQueries(queryClient),
  });
};

export const useUpdateUnmatchedCodeSignerGroupStatuses = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ query, payload }) =>
      postUnmatchedCodeSignerGroupStatuses({ customerEnvId, query, payload }),
    onSuccess: () => invalidateUnmatchedQueries(queryClient),
  });
};

export const useUpdateUnmatchedMatchedAppGroupStatuses = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ query, payload }) =>
      postUnmatchedMatchedAppGroupStatuses({ customerEnvId, query, payload }),
    onSuccess: () => invalidateUnmatchedQueries(queryClient),
  });
};

export const useUnmatchedCodeSignerGroupFileCount = ({ payload, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_CODE_SIGNER_GROUP_FILE_COUNT_QUERY_KEY, payload, customerEnvId],
    () => getUnmatchedCodeSignerGroupFileCount({ customerEnvId, payload }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUnmatchedMatchedAppGroupFileCount = ({ payload, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_MATCHED_APP_GROUP_FILE_COUNT_QUERY_KEY, payload, customerEnvId],
    () => getUnmatchedMatchedAppGroupFileCount({ customerEnvId, payload }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUnmatchedFileSummary = ({ statusId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_FILE_SUMMARY_QUERY_KEY, statusId, customerEnvId],
    ({ signal }) =>
      getUnmatchedFileSummary({
        customerEnvId,
        statusId,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!statusId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUnmatchedFileSigners = ({ fileId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_FILE_SIGNERS_QUERY_KEY, fileId, customerEnvId],
    ({ signal }) =>
      getUnmatchedFileSigners({
        customerEnvId,
        fileId,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!fileId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUnmatchedFileMatchedApps = ({ fileId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_FILE_MATCHED_APPS_QUERY_KEY, fileId, customerEnvId],
    ({ signal }) =>
      getUnmatchedFileMatchedApps({
        customerEnvId,
        fileId,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!fileId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteUnmatchedFileEvents = ({ fileId, limit = 10, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_UNMATCHED_FILE_EVENTS_QUERY_KEY, fileId, customerEnvId, limit],
    ({ pageParam = 1, signal }) =>
      getUnmatchedFileEvents({
        customerEnvId,
        fileId,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!fileId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useInfiniteUnmatchedCodeSigners = ({ apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_UNMATCHED_CODE_SIGNERS_QUERY_KEY, apiQuery, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getUnmatchedCodeSigners({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useUnmatchedCodeSignerFiles = ({ apiQuery, page = 1, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_CODE_SIGNER_FILES_QUERY_KEY, apiQuery, page, limit, customerEnvId],
    ({ signal }) =>
      getUnmatchedCodeSignerFiles({
        customerEnvId,
        page,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );
};

export const useInfiniteUnmatchedMatchedApps = ({ apiQuery, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WDAC_UNMATCHED_MATCHED_APPS_QUERY_KEY, apiQuery, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getUnmatchedMatchedApps({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useUnmatchedMatchedAppFiles = ({ apiQuery, page = 1, limit, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WDAC_UNMATCHED_MATCHED_APP_FILES_QUERY_KEY, apiQuery, page, limit, customerEnvId],
    ({ signal }) =>
      getUnmatchedMatchedAppFiles({
        customerEnvId,
        page,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );
};

export const useWin32AppPolicy = ({ globalApplicationVersionId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WIN32_APP_POLICY_QUERY_KEY, globalApplicationVersionId, customerEnvId],
    ({ signal }) => getWin32AppPolicy({ customerEnvId, globalApplicationVersionId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useDownloadWin32AppCatalog = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation({
    mutationFn: ({ globalApplicationVersionId }) =>
      getWin32AppCatalogZip({ customerEnvId, globalApplicationVersionId }),
  });
};

export const useWin32FileSetDetail = ({
  globalApplicationVersionId,
  fileSetId,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WIN32_FILE_SET_DETAIL_QUERY_KEY, globalApplicationVersionId, fileSetId, customerEnvId],
    ({ signal }) =>
      getWin32FileSetDetail({
        customerEnvId,
        globalApplicationVersionId,
        fileSetId,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId && !!fileSetId,
      refetchOnWindowFocus: false,
    },
  );
};

// The modal's content sections are the same paged shape over different parts of a file set, so one
// hook serves them all and the caller names the section and the array its response carries.
export const useInfiniteWin32FileSetSection = ({
  globalApplicationVersionId,
  fileSetId,
  section,
  itemsKey,
  limit = 25,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [
      WIN32_FILE_SET_SECTION_QUERY_KEY,
      section,
      globalApplicationVersionId,
      fileSetId,
      customerEnvId,
    ],
    ({ pageParam = 1, signal }) =>
      getWin32FileSetSection({
        customerEnvId,
        globalApplicationVersionId,
        fileSetId,
        section,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId && !!fileSetId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        // The detail sections carry their total as `count`, the paged file list as `totalCount`.
        const total = lastPage.count ?? lastPage.totalCount ?? 0;
        const loaded = pages.reduce((acc, page) => acc + (page[itemsKey]?.length || 0), 0);
        return loaded < total ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useWin32AppFileDetail = ({ globalApplicationVersionId, fileId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WIN32_APP_FILE_DETAIL_QUERY_KEY, globalApplicationVersionId, fileId, customerEnvId],
    ({ signal }) =>
      getWin32AppFileDetail({ customerEnvId, globalApplicationVersionId, fileId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId && !!fileId,
      refetchOnWindowFocus: false,
    },
  );
};

// A file has a handful of signers at most, so they come back whole rather than paged.
export const useWin32AppFileSigners = ({ globalApplicationVersionId, fileId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [
      WIN32_APP_FILE_SECTION_QUERY_KEY,
      'signers',
      globalApplicationVersionId,
      fileId,
      customerEnvId,
    ],
    ({ signal }) =>
      getWin32AppFileSigners({ customerEnvId, globalApplicationVersionId, fileId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId && !!fileId,
      refetchOnWindowFocus: false,
    },
  );
};

// The file modal's paged tables, over a file's paths and its devices.
export const useInfiniteWin32AppFileSection = ({
  globalApplicationVersionId,
  fileId,
  section,
  limit = 25,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WIN32_APP_FILE_SECTION_QUERY_KEY, section, globalApplicationVersionId, fileId, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getWin32AppFileSection({
        customerEnvId,
        globalApplicationVersionId,
        fileId,
        section,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId && !!fileId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
        return loaded < (lastPage.totalCount ?? 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useWin32AppFileSets = ({ globalApplicationVersionId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [WIN32_APP_FILE_SETS_QUERY_KEY, globalApplicationVersionId, customerEnvId],
    ({ signal }) => getWin32AppFileSets({ customerEnvId, globalApplicationVersionId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useWin32AppWdacFileCounts = ({
  globalApplicationVersionId,
  reportedWindow,
  isSigned,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [
      WIN32_APP_WDAC_FILE_COUNTS_QUERY_KEY,
      globalApplicationVersionId,
      reportedWindow,
      isSigned,
      customerEnvId,
    ],
    ({ signal }) =>
      getWin32AppWdacFileCounts({
        customerEnvId,
        globalApplicationVersionId,
        reportedWindow,
        isSigned,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
    },
  );
};

const getNextTotalCountPage = (lastPage, pages) => {
  const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
  return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
};

export const useInfiniteWin32AppWdacFiles = ({
  globalApplicationVersionId,
  isCovered,
  reportedWindow,
  isSigned,
  search,
  sortColumn,
  sortDesc,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [
      WIN32_APP_WDAC_FILES_QUERY_KEY,
      globalApplicationVersionId,
      isCovered,
      reportedWindow,
      isSigned,
      search,
      sortColumn,
      sortDesc,
      customerEnvId,
    ],
    ({ pageParam = 1, signal }) =>
      getWin32AppWdacFiles({
        customerEnvId,
        globalApplicationVersionId,
        isCovered,
        reportedWindow,
        isSigned,
        search,
        sortColumn,
        sortDesc,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam: getNextTotalCountPage,
    },
  );
};

export const useInfiniteWin32AppFiles = ({
  globalApplicationVersionId,
  sources,
  isSigned,
  search,
  sortColumn,
  sortDesc,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [
      WIN32_APP_FILES_QUERY_KEY,
      globalApplicationVersionId,
      sources,
      isSigned,
      search,
      sortColumn,
      sortDesc,
      customerEnvId,
    ],
    ({ pageParam = 1, signal }) =>
      getWin32AppFiles({
        customerEnvId,
        globalApplicationVersionId,
        sources,
        isSigned,
        search,
        sortColumn,
        sortDesc,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam: getNextTotalCountPage,
    },
  );
};

export const useInfiniteWin32AppFileSets = ({
  globalApplicationVersionId,
  statuses,
  search,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [
      WIN32_APP_FILE_SET_LIST_QUERY_KEY,
      globalApplicationVersionId,
      statuses,
      search,
      customerEnvId,
    ],
    ({ pageParam = 1, signal }) =>
      getWin32AppFileSetList({
        customerEnvId,
        globalApplicationVersionId,
        statuses,
        search,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam: getNextTotalCountPage,
    },
  );
};

export const useInfiniteWin32AppWdacImpactedDevices = ({
  globalApplicationVersionId,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [WIN32_APP_WDAC_IMPACTED_DEVICES_QUERY_KEY, globalApplicationVersionId, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getWin32AppWdacImpactedDevices({
        customerEnvId,
        globalApplicationVersionId,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam: getNextTotalCountPage,
    },
  );
};

// A status save moves the app's imports and catalog when it trusts the app, which picks its imports,
// can create one and versions the catalog, or untrusts it, which switches its imports off.
export const statusUpdateMovesImports = ({
  status,
  import: importScope,
  enabledCustomerEnvironmentApplicationIds,
  basePolicyIds,
}) =>
  !!importScope ||
  enabledCustomerEnvironmentApplicationIds != null ||
  basePolicyIds != null ||
  status === APPLICATION_TRUST_STATUS.Untrusted;

// A new customer application joins the app's identity, so everything that reads the identity is
// refetched along with the WDAC files compared against it. Trusting moves the status date the
// reported files and devices are counted from, and versions the catalog the policy state reads.
export const invalidateWin32AppImportQueries = queryClient =>
  [
    WIN32_APP_POLICY_QUERY_KEY,
    MANAGED_BASE_POLICIES_QUERY_KEY,
    WIN32_APP_WDAC_FILE_COUNTS_QUERY_KEY,
    WIN32_APP_WDAC_FILES_QUERY_KEY,
    WIN32_APP_FILE_SETS_QUERY_KEY,
    WIN32_APP_FILE_SET_LIST_QUERY_KEY,
    WIN32_APP_FILES_QUERY_KEY,
    WIN32_APP_WDAC_IMPACTED_DEVICES_QUERY_KEY,
    WDAC_APP_QUERY_KEY,
    WDAC_RESULTS_QUERY_KEY,
    APP_PORTFOLIO_QUERY_KEY,
    APP_PORTFOLIO_VERSIONS_QUERY_KEY,
    APP_PORTFOLIO_VERSION_LIST_QUERY_KEY,
    APP_COUNTS_KEY,
    RESULTS_TAB_DATA_COUNTS_KEY,
  ].forEach(queryKey => queryClient.invalidateQueries({ queryKey: [queryKey], exact: false }));

const nextPortfolioPage = (lastPage, pages) => {
  const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
  return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
};

export const useInfiniteAppPortfolio = ({
  search,
  statuses,
  sources,
  sort,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [APP_PORTFOLIO_QUERY_KEY, search, statuses, sources, sort, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getAppPortfolio({
        customerEnvId,
        page: pageParam,
        limit,
        search,
        statuses,
        sources,
        sortColumn: sort?.column,
        sortDesc: sort?.direction === 'desc',
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: nextPortfolioPage,
    },
  );
};

export const useAppPortfolioVersions = ({
  globalApplicationVersionId,
  statuses,
  sources,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [
      APP_PORTFOLIO_VERSIONS_QUERY_KEY,
      globalApplicationVersionId,
      statuses,
      sources,
      customerEnvId,
    ],
    ({ signal }) =>
      getAppPortfolioVersions({
        customerEnvId,
        globalApplicationVersionId,
        statuses,
        sources,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!globalApplicationVersionId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteAppPortfolioVersionList = ({
  search,
  statuses,
  sources,
  sort,
  limit,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [APP_PORTFOLIO_VERSION_LIST_QUERY_KEY, search, statuses, sources, sort, customerEnvId],
    ({ pageParam = 1, signal }) =>
      getAppPortfolioVersionList({
        customerEnvId,
        page: pageParam,
        limit,
        search,
        statuses,
        sources,
        sortColumn: sort?.column,
        sortDesc: sort?.direction === 'desc',
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: nextPortfolioPage,
    },
  );
};

// Every portfolio list is refetched: the version rows for the status itself, the groups for the
// last-acted-on date they take from it.
export const useUpdateAppPortfolioStatus = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: variables => postGlobalAppStatus({ customerEnvId, ...variables }),
    onSuccess: (_data, variables) => {
      if (statusUpdateMovesImports(variables)) {
        invalidateWin32AppImportQueries(queryClient);
        return;
      }

      queryClient.invalidateQueries([APP_PORTFOLIO_VERSIONS_QUERY_KEY]);
      queryClient.invalidateQueries([APP_PORTFOLIO_VERSION_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([APP_PORTFOLIO_QUERY_KEY]);
      queryClient.invalidateQueries([APP_COUNTS_KEY]);
    },
  });
};
